import React, { PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { MetaradarQueryKeys } from '@app/model/metaradar/service/MetaradarQueryKeys';
import { useGetMetaradarRefreshStatusQuery } from '@app/model/metaradar/service/MetaradarServiceHooks';
import { MetaradarRefreshState } from '@app/model/metaradar/MetaradarRefreshStatus';
import { useCompanyHotelsLiteQuery } from '@app/model/company_hotel/service/CompanyHotelServiceHooks';
import { metaradarRefreshEnd, metaradarRefreshMessage } from './MetaradarRefreshWatch.utilities';

type MetaradarRefreshWatchContextValue = {
	// Watch the refresh of a company hotel until it ends, then show one toast on the current page. With no request id,
	// the watch takes the id of the running refresh as soon as its status gives one.
	watch: (companyHotelId: number, requestId: number | undefined) => void,
};

export const MetaradarRefreshWatchContext = React.createContext<MetaradarRefreshWatchContextValue>({
	watch: () => undefined,
});

export const useMetaradarRefreshWatch = () => useContext(MetaradarRefreshWatchContext);

/**
 * A Metaradar refresh runs in the background and never locks the app (ROO2-232). This provider stays mounted under
 * DomainRouter, so the end of a refresh shows a toast on any page and for any hotel, not only on the Metaradar tab.
 * It sits above CompanyHotelsProvider, which unmounts its children while a newly selected hotel loads.
 * The watch lives in the browser memory: after a reload, the Metaradar tab watches a running refresh again.
 */
const MetaradarRefreshWatchProvider: React.FunctionComponent<PropsWithChildren> = ({ children }) => {
	// The watched request of each company hotel (null: no request id yet): one refresh at a time for each hotel.
	const [watched, setWatched] = useState<Record<number, number | null>>({});
	const endedRequestIds = useRef(new Set<number>());

	const watch = useCallback((companyHotelId: number, requestId: number | undefined) => {
		if (requestId !== undefined && endedRequestIds.current.has(requestId)) return;
		setWatched(previous => {
			// A watch with no request id never replaces a watch that has one.
			if (requestId === undefined) return companyHotelId in previous ? previous : { ...previous, [companyHotelId]: null };
			return previous[companyHotelId] === requestId ? previous : { ...previous, [companyHotelId]: requestId };
		});
	}, []);

	const isEnded = useCallback((requestId: number) => endedRequestIds.current.has(requestId), []);

	const end = useCallback((companyHotelId: number, watchedRequestId: number | null, endedRequestId: number | undefined) => {
		if (endedRequestId !== undefined) endedRequestIds.current.add(endedRequestId);
		setWatched(previous => {
			if (previous[companyHotelId] !== watchedRequestId) return previous;
			const rest = { ...previous };
			delete rest[companyHotelId];
			return rest;
		});
	}, []);

	const contextValue = useMemo(() => ({ watch }), [watch]);

	return <MetaradarRefreshWatchContext.Provider value={contextValue}>
		{children}
		{Object.entries(watched).map(([companyHotelId, requestId]) =>
			<MetaradarRefreshWatcher key={companyHotelId} companyHotelId={Number(companyHotelId)} requestId={requestId}
				onRequestId={watch} isEnded={isEnded} onEnd={end} />)}
	</MetaradarRefreshWatchContext.Provider>;
};

const MetaradarRefreshWatcher: React.FunctionComponent<{
	companyHotelId: number,
	requestId: number | null,
	onRequestId: (companyHotelId: number, requestId: number) => void,
	isEnded: (requestId: number) => boolean,
	onEnd: (companyHotelId: number, watchedRequestId: number | null, endedRequestId: number | undefined) => void,
}> = ({ companyHotelId, requestId, onRequestId, isEnded, onEnd }) => {
	const toaster = useToaster();
	const queryClient = useQueryClient();
	// The hotel list that CompanyHotelsProvider loads; the cached list is enough for the name.
	const { data: companyHotels } = useCompanyHotelsLiteQuery({ staleTime: Infinity });
	// The status query polls while the refresh runs; the Metaradar tab reads the same cache entry.
	const status = useGetMetaradarRefreshStatusQuery(companyHotelId).data;

	useEffect(() => {
		if (requestId === null && status?.status === MetaradarRefreshState.IN_PROGRESS && status.executionState) {
			onRequestId(companyHotelId, status.executionState.requestId);
			return;
		}
		const ended = metaradarRefreshEnd(status, requestId ?? undefined);
		if (ended === undefined) return;
		const endedRequestId = requestId ?? status?.executionState?.requestId;
		queryClient.invalidateQueries({ queryKey: MetaradarQueryKeys.companyHotel(companyHotelId) });
		// A watch with no request id can meet a refresh that already gave its toast.
		const isRepeated = requestId === null && endedRequestId !== undefined && isEnded(endedRequestId);
		const hotelName = companyHotels?.find(companyHotel => companyHotel.id === companyHotelId)?.mainHotelName;
		if (!isRepeated && ended === 'success') toaster.success('Metaradar', metaradarRefreshMessage(ended, hotelName));
		if (!isRepeated && ended === 'failure') toaster.error('Metaradar', metaradarRefreshMessage(ended, hotelName));
		onEnd(companyHotelId, requestId, endedRequestId);
	}, [status, requestId, companyHotelId]);

	return null;
};

export default MetaradarRefreshWatchProvider;
